import { gql } from "@apollo/client";
import { Shopify } from "@models/shopify.model";
import { ShopifyToken } from "@providers/profile/ProfileProvider.model";
import { Dispatch } from "react";

export enum OrderAction {
  SET_ORDERS = "set_orders",
  FAILED_FETCH = "fetch_order_fail",
}

export interface OrderDispatch {
  type: OrderAction;
  orders?: Shopify.Order[];
  message?: Error;
}

export interface OrderProviderState {
  orders: {
    [x: string]: Shopify.Order;
  };
}

export interface OrderContext {
  dispatch: Dispatch<OrderDispatch>;
  getOrders: (
    shopifyToken: ShopifyToken,
    dispatch: Dispatch<OrderDispatch>
  ) => Promise<Shopify.Order[]>;
  state: OrderProviderState;
}

export const ORDER_QUERY = gql`
  query Order($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      orders(first: 50) {
        edges {
          node {
            id
            orderNumber
            originalTotalPrice {
              amount
              currencyCode
            }
            processedAt
            shippingAddress {
              address1
              address2
              city
              firstName
              id
              lastName
              province
              provinceCode
              zip
            }
            shippingDiscountAllocations {
              allocatedAmount {
                amount
                currencyCode
              }
              discountApplication {
                targetType
                value {
                  ... on MoneyV2 {
                    amount
                    currencyCode
                  }
                  ... on PricingPercentageValue {
                    percentage
                  }
                }
              }
            }
            successfulFulfillments(first: 10) {
              trackingInfo {
                number
                url
              }
              fulfillmentLineItems(first: 100) {
                edges {
                  node {
                    quantity
                    lineItem {
                      title
                      variant {
                        id
                        title
                        variantOption1: metafield(namespace: "custom", key: "variant_option_1") {
                          type
                          value
                        }
                        variantOption2: metafield(namespace: "custom", key: "variant_option_2") {
                          type
                          value
                        }
                        product {
                          tags
                          cartDescription: metafield(namespace: "custom", key: "cart_description") {
                            type
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            subtotalPrice {
              amount
              currencyCode
            }
            totalPrice {
              amount
              currencyCode
            }
            totalShippingPrice {
              amount
              currencyCode
            }
            totalTax {
              amount
              currencyCode
            }
            lineItems(first: 100) {
              edges {
                node {
                  title
                  quantity
                  discountedTotalPrice {
                    amount
                    currencyCode
                  }
                  originalTotalPrice {
                    amount
                    currencyCode
                  }
                  discountAllocations {
                    allocatedAmount {
                      amount
                      currencyCode
                    }
                    discountApplication {
                      targetType
                      value {
                        ... on MoneyV2 {
                          amount
                          currencyCode
                        }
                        ... on PricingPercentageValue {
                          percentage
                        }
                      }
                    }
                  }
                  variant {
                    id
                    title
                    variantOption1: metafield(namespace: "custom", key: "variant_option_1") {
                      type
                      value
                    }
                    variantOption2: metafield(namespace: "custom", key: "variant_option_2") {
                      type
                      value
                    }
                    product {
                      tags
                      cartDescription: metafield(namespace: "custom", key: "cart_description") {
                        type
                        value
                      }
                    }
                  }
                }
              }
            }
            discountApplications(first: 5) {
              edges {
                node {
                  value {
                    ... on MoneyV2 {
                      amount
                      currencyCode
                    }
                    ... on PricingPercentageValue {
                      percentage
                    }
                  }
                  allocationMethod
                }
              }
            }
          }
        }
      }
    }
  }
`;
